const sheetMenu = document.createElement("template");
sheetMenu.innerHTML = `  
      <slot name="sheet-trigger"></slot>
      <slot name="sheet-content"></slot>
    `;

class SheetMenu extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      sheetMenu.content.cloneNode(true)
    );

    this.isOpen = false;
    this._onTriggerClick = this._onTriggerClick.bind(this);

    // Create sheet overlay here once
    // createElement("sheet-overlay") creates an instance of the custom element, unlike createElement("template") which creates a blueprint for content.
    this.sheetOverlay = document.createElement("sheet-overlay");
    this.sheetOverlay.setAttribute("data-state", "closed");
    this.sheetOverlay.setAttribute("role", "presentation");
    this.sheetOverlay.addEventListener("click", () => this.close());
  }

  connectedCallback() {
    this.sheetTrigger = this.querySelector("sheet-trigger");
    this.sheetContent = this.querySelector("sheet-content");

    if (!this.sheetTrigger || !this.sheetContent) {
      console.warn("<sheet> needs a <sheet-trigger> and <sheet-content>");
      return;
    }

    // Set up direction-based styling
    this._setupDirection();

    // Detach the content so it never lives in the DOM until open()
    this.sheetOverlay.remove();
    this.sheetContent.remove();

    this._updateAriaExpanded();

    this.sheetTrigger.addEventListener("click", this._onTriggerClick);
  }

  disconnectedCallback() {
    if (this.sheetOverlay && this.sheetOverlay.parentNode) {
      this.sheetOverlay.remove();
    }
    if (this.sheetContent && this.sheetContent.parentNode) {
      this.sheetContent.remove();
    }

    this.sheetTrigger?.removeEventListener("click", this._onTriggerClick);
  }

  _setupDirection() {
    // Get the direction from the data attribute, default to 'right'
    const direction = this.getAttribute("data-direction") || "right";

    // Apply direction attribute to sheet content for CSS targeting
    this.sheetContent.setAttribute("data-direction", direction);
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  _updateAriaExpanded() {
    if (this.sheetTrigger) {
      this.sheetTrigger.setAttribute("aria-expanded", String(this.isOpen));
    }
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this._updateAriaExpanded();

    // Append the overlay you created in ctor
    document.body.appendChild(this.sheetOverlay);

    // Move your content out to <body>
    if (this.sheetContent.parentNode !== document.body) {
      document.body.appendChild(this.sheetContent);
    }

    // Trigger reflow to ensure animations start properly after state changes.
    this.sheetOverlay.offsetHeight;
    this.sheetContent.offsetHeight;

    // Update states to open, triggering CSS animations.
    this.sheetOverlay.setAttribute("data-state", "open");
    this.sheetContent.setAttribute("data-state", "open");

    document.body.setAttribute("data-scroll-locked", "");

    this.dispatchEvent(
      new CustomEvent("sheet:open", {
        bubbles: true,
        detail: { sheet: this },
      })
    );
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this._updateAriaExpanded();

    this.sheetOverlay.setAttribute("data-state", "closed");
    this.sheetContent.setAttribute("data-state", "closed");

    setTimeout(() => {
      if (this.sheetOverlay.parentNode) {
        // document.body.removeChild(this.sheetOverlay);
        this.sheetOverlay.remove();
      }

      if (this.sheetContent.parentNode === document.body) {
        // document.body.removeChild(this.sheetContent);
        this.sheetContent.remove();
      }

      document.body.removeAttribute("data-scroll-locked");
    }, 300);

    this.dispatchEvent(
      new CustomEvent("sheet:close", {
        bubbles: true,
        detail: { sheet: this },
      })
    );
  }

  _onTriggerClick(e) {
    e.preventDefault();
    this.toggle();
  }
}

customElements.define("sheet-menu", SheetMenu);

const sheetTrigger = document.createElement("template");
sheetTrigger.innerHTML = `
      <slot></slot>
`;

class SheetTrigger extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      sheetTrigger.content.cloneNode(true)
    );
    this.setAttribute("slot", "sheet-trigger");
    this.setAttribute("aria-haspopup", "dialog");
    this.setAttribute("role", "button");
    this.setAttribute("tabindex", "0");
  }

  connectedCallback() {
    // Handle keyboard activation
    this.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.click();
      }
    });
  }
}

customElements.define("sheet-trigger", SheetTrigger);

const sheetContent = document.createElement("template");
sheetContent.innerHTML = `
      <slot></slot>
    `;

class SheetContent extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      sheetContent.content.cloneNode(true)
    );

    this.setAttribute("slot", "sheet-content");
    this.setAttribute("role", "dialog");

    this.setAttribute("data-state", "closed");
  }
}

customElements.define("sheet-content", SheetContent);

const sheetHeader = document.createElement("template");
sheetHeader.innerHTML = `
      <slot></slot>
    `;

class SheetHeader extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      sheetHeader.content.cloneNode(true)
    );

    this.setAttribute("role", "dialog");
  }
}

customElements.define("sheet-header", SheetHeader);

const sheetTitle = document.createElement("template");
sheetTitle.innerHTML = `
      <slot></slot>
    `;

class SheetTitle extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      sheetTitle.content.cloneNode(true)
    );

    this.setAttribute("role", "heading");
  }
}

customElements.define("sheet-title", SheetTitle);

const sheetDescription = document.createElement("template");
sheetDescription.innerHTML = `
      <slot></slot>
    `;

class SheetDescription extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      sheetDescription.content.cloneNode(true)
    );
  }
}

customElements.define("sheet-description", SheetDescription);

const sheetFooter = document.createElement("template");
sheetFooter.innerHTML = `
      <slot></slot>
    `;

class SheetFooter extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      sheetFooter.content.cloneNode(true)
    );
  }
}

customElements.define("sheet-footer", SheetFooter);

const sheetSeparator = document.createElement("template");
sheetSeparator.innerHTML = ``;

class SheetSeparator extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      sheetSeparator.content.cloneNode(true)
    );
    this.setAttribute("role", "separator");
  }
}

customElements.define("sheet-separator", SheetSeparator);

const sheetClose = document.createElement("template");
sheetClose.innerHTML = `
  <slot></slot>
`;

class SheetClose extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).append(
      sheetClose.content.cloneNode(true)
    );

    this.setAttribute("role", "button");
    this.setAttribute("tabindex", "0");
    this.setAttribute("aria-label", "Close sheet");

    this._onClick = this._onClick.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this._onClick);
    this.addEventListener("keydown", this._onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this._onClick);
    this.removeEventListener("keydown", this._onKeydown);
  }

  _onClick(e) {
    e.preventDefault();
    e.stopPropagation(); // Prevent triggering overlay click
    this._closeSheet();
  }

  _onKeydown(e) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this._closeSheet();
    }
  }

  _closeSheet() {
    // Find the parent sheet-content component
    const sheetContent = this.closest("sheet-content");

    if (!sheetContent) {
      console.warn("<sheet-close> must be inside a <sheet-content>");
      return;
    }

    // Find the parent sheet-menu by traversing up the DOM
    // Since sheet-content gets moved to document.body when open,
    // we need to find the sheet-menu that contains this content
    const allSheetMenus = document.querySelectorAll("sheet-menu");

    for (const sheetMenu of allSheetMenus) {
      if (sheetMenu.sheetContent === sheetContent) {
        sheetMenu.close();
        return;
      }
    }

    console.warn("Could not find parent sheet-menu");
  }
}

customElements.define("sheet-close", SheetClose);
